import {type MetaFunction} from '@remix-run/node';
import Competition, {type CompetitionContent} from '#/app/components/Competition.jsx';
import {Image} from '#/app/components/Image.jsx';
import {COMPARISON_ROUTE4ME} from '#/app/content/alternatives/comparison.js';
import {createMetaHandler} from '#/app/helpers/createMetaHandler.js';
import {createMetaTags} from '#/app/helpers/createMetaTags.js';

const CONTENT: CompetitionContent = {
  company: 'Route4Me',
  comparisonIndex: COMPARISON_ROUTE4ME,
  highlights: [
    {
      description: [
        <div className="space-y-4" key="1">
          <p>
            No more unnecessary miles! You’ll get the shortest, fastest, and most convenient delivery routes. Stop wasting hours when you
            can plan routes in seconds.
          </p>
          <ul className="ml-4 space-y-2 [&>*]:pl-4" style={{listStyleType: '"✓"'}}>
            <li>Significantly reduce mileage</li>
            <li>No more unnecessary idle time between deliveries</li>
            <li>Pay for 20% less fuel </li>
            <li>Produce fewer CO2 emissions</li>
            <li>Optimize thousands of routes in seconds</li>
          </ul>
        </div>,
      ],
      image: () => (
        <div className="flex w-11/12 items-center justify-center sm:w-6/12">
          <Image
            alt="ufleet native driver app screenshot showing a list of tasks"
            src="/features-control-center-1.png"
            style={{maxWidth: 500}}
          />
        </div>
      ),
      subtitle: 'Pay less, do more',
      title: 'Smart and cost-efficient deliveries at scale',
    },
    {
      description: [
        <div className="space-y-4" key="1">
          <p>
            Ditch the manual route optimization—it’s a thing of the past. It’s time to automate your deliveries and take your service to the
            next level.
          </p>
          <ul className="ml-4 space-y-2 [&>*]:pl-4" style={{listStyleType: '"✓"'}}>
            <li>Future-proof your delivery operations and administration</li>
            <li>Impress customers with technology-powered live tracking, status updates, and feedback forms</li>
            <li>Gain a competitive edge over your competitors</li>
            <li>Position your business as a modern and tech-savvy employer</li>
            <li>Use technology to gain important insights and make data-driven decision</li>
          </ul>
        </div>,
      ],
      image: () => (
        <div className="flex w-11/12 items-center justify-center sm:w-8/12">
          <Image
            alt="ufleet native driver app screenshot showing a list of tasks"
            src="/features-integrations-2.png"
            style={{maxWidth: 500}}
          />
        </div>
      ),
      subtitle: 'Let the AI do the work',
      title: 'Delivery operations that follow the latest trends and standards',
    },
    {
      description: [
        <div className="space-y-4" key="1">
          <p>Provide a delivery experience to remember—timely, convenient, and transparent!</p>
          <ul className="ml-4 space-y-2 [&>*]:pl-4" style={{listStyleType: '"✓"'}}>
            <li>Keep customers informed with live tracking and notifications</li>
            <li>Collect immediate customer feedback upon delivery</li>
            <li>Track delays or missed deliveries, and act in real-time</li>
            <li>Promote your brand, loyalty programs, coupons, and more</li>
            <li>Enable seamless communication between drivers and customers</li>
          </ul>
        </div>,
      ],
      image: () => (
        <div className="flex w-11/12 items-center justify-center sm:w-full">
          <Image
            alt="ufleet native driver app screenshot showing a list of tasks"
            src="/features-customer-portal-2.png"
            style={{maxWidth: 500}}
          />
        </div>
      ),
      subtitle: 'Delight your customers',
      title: 'Stellar delivery experience your customers will love',
    },
  ],
  quote: {
    image: '/case-studies/obi/tomasz-starczynowski.jpg',
    person: 'Tomasz Starczynowski',
    quote: `Today customers are not only comparing the experience with our direct competitors - they are comparing it with the best delivery experience ever. Ufleet has given us the technological edge to improve our home delivery offering and stay ahead in this field.`,
    title: 'Sales Director, OBI Board Member',
  },
  reasons: [
    {
      content: [
        '10 route optimization parameters that no one else offers',
        'Advanced AI algorithms that calculate routes tremendously smart',
        'Change routes, edit details, and re-optimize in real-time',
      ],
      title: 'Precise route optimization',
    },
    {
      content: [
        'Tailor-made features to fit your specific needs',
        'Customized integrations so you can connect the apps you already use',
        'White-label Customer Portal that helps you build a strong brand presence',
      ],
      title: 'Flexibility & Customization',
    },
    {
      content: [
        'Guided system setup and onboarding',
        'We’ll be there for you to show you how to make the most out of Ufleet',
        'Whenever you have a problem or question, our team will be there for you at no additional cost',
      ],
      title: 'Exceptional Support',
    },
  ],
};

export const meta: MetaFunction = createMetaHandler(() => {
  const {company} = CONTENT;
  const slug = company.toLowerCase();
  return createMetaTags({
    description: `Compare the top delivery route optimization platforms – ${company} vs Ufleet – to decide which software is right for your business.`,
    title: `Best ${company} Alternative: Try Ufleet for Route Optimization - Ufleet`,
    url: `https://ufleet.io/${slug}-alternative-ufleet-${slug}`,
  });
});

export default function RoutificAlternative() {
  return <Competition {...CONTENT} />;
}
